/**
 * S0 keepsake job locator (PC-B rev 9, section 10a.2).
 *
 * The notification email links `/?keepsakeJob=<uuid>`. The client
 * recognizes ONLY this query key, validates a canonical UUID, stores the
 * locator ONLY in tab-scoped sessionStorage
 * (`adia-keepsake-open-job-v1`), and removes it from browser history
 * IMMEDIATELY (before React Router can observe it). It never enters
 * durable IDB or localStorage. Once central auth is ready, the consumer
 * DELETES the stored locator before making its single expected-owner
 * status request; signed-out state may retain it only within the tab
 * until sign-in; a wrong account receives the unified 404. Delivery
 * requires the same authenticated expected-owner check, so the job id is
 * a locator, never a capability.
 */

import {
  JOB_LOCATOR_QUERY_KEY,
  JOB_LOCATOR_SESSION_KEY,
} from './constants';

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;

/**
 * Capture + strip the locator from the URL. Called from main.tsx BEFORE
 * React renders (so history is clean before the router initializes).
 */
export function captureJobLocatorFromUrl(): void {
  if (typeof window === 'undefined') return;
  try {
    const url = new URL(window.location.href);
    const raw = url.searchParams.get(JOB_LOCATOR_QUERY_KEY);
    if (raw === null) return;
    url.searchParams.delete(JOB_LOCATOR_QUERY_KEY);
    // Remove from browser history regardless of validity.
    window.history.replaceState(window.history.state, '', url.toString());
    if (UUID_RE.test(raw)) {
      sessionStorage.setItem(JOB_LOCATOR_SESSION_KEY, raw);
    }
  } catch {
    // sessionStorage unavailable (private mode): the locator is dropped;
    // the user can still reach the keepsake through the app.
  }
}

/** Read WITHOUT consuming (signed-out tabs retain until sign-in). */
export function peekJobLocator(): string | null {
  try {
    const v = sessionStorage.getItem(JOB_LOCATOR_SESSION_KEY);
    return v !== null && UUID_RE.test(v) ? v : null;
  } catch {
    return null;
  }
}

/** Consume: DELETE the stored locator (before the single status request). */
export function consumeJobLocator(): string | null {
  try {
    const v = peekJobLocator();
    sessionStorage.removeItem(JOB_LOCATOR_SESSION_KEY);
    return v;
  } catch {
    return null;
  }
}
